import { useCallback, useEffect, useRef, useState } from 'react';
import {
  getReceiverCapabilities,
  getReceiverConnectionEvents,
  getReceiverDailyBreakdown,
  getReceiverHeatmap,
  getReceiverHourlyBreakdown,
  getReceiverReportHeatmap,
  getReceiverSiteOverview,
  getReceiverStats,
  getReceiverStatsLatest,
  getTrackedAircraftList,
} from '../utils/httpApi';
import { isApiSuccess } from '../utils/api';
import { getAccessToken } from '../utils/auth';

/** Metrics the live-health section plots. */
const PERF_METRICS = [
  'msg_rx_rate', 'msg_dropped',
  // Mode-S-only aircraft (CRC-verified DF11, no DF17) — receiver 1.05.26+, absent before.
  'aircraft_now', 'aircraft_modes_now', 'aircraft_peak_1m',
  'max_range_nm',
  'rssi_min_dbfs', 'rssi_avg_dbfs', 'rssi_max_dbfs', 'noise_floor_dbfs',
  'current_gain_db',
  'cpu_pct', 'mem_pct', 'temp_c',
  'uptime_s',
];

const EMPTY_DATA = {
  caps: null, overview: null, series: {}, latest: null, events: [],
  daily: [], hourly: [], coverage: null, dayAircraft: [], dayRangeSamples: [],
};

/**
 * All Statistics-page fetching, keyed on (receiver, range).
 *
 * Three deliberate properties:
 *
 *  - Cancel-safe. Switching receiver or range mid-flight must not let a stale
 *    response overwrite a newer one, so every request carries the generation it
 *    belongs to and late arrivals are dropped.
 *  - The heavy queries are separate. The report heatmap is a full Greptime scan
 *    and the tracked-aircraft list can be thousands of rows; neither should
 *    hold up the rest of the page, so they resolve into their own state.
 *  - Failures are per-slice. One endpoint erroring leaves the others rendered
 *    rather than blanking the page, and the error is surfaced, not swallowed.
 */
export function useReceiverStats(receiverUuid, range, refreshSeconds) {
  const [data, setData] = useState(EMPTY_DATA);
  const [reportHeatmap, setReportHeatmap] = useState(null);
  const [reportLoading, setReportLoading] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [updatedAt, setUpdatedAt] = useState(null);

  // Bumped on every load; responses from an older generation are discarded.
  const genRef = useRef(0);
  const aliveRef = useRef(true);
  useEffect(() => () => { aliveRef.current = false; }, []);

  const load = useCallback(async () => {
    if (!receiverUuid) { setData(EMPTY_DATA); setReportHeatmap(null); return; }

    const gen = ++genRef.current;
    const fresh = () => aliveRef.current && gen === genRef.current;

    setLoading(true);
    setError(null);
    const token = getAccessToken();

    // settled, not all: one failing slice must not blank the page.
    const results = await Promise.allSettled([
      getReceiverCapabilities(receiverUuid, token),
      getReceiverSiteOverview(receiverUuid, token),
      getReceiverStats(receiverUuid, range.fromIso, range.toIso,
        range.stepSeconds, PERF_METRICS, token),
      getReceiverStatsLatest(receiverUuid, token),
      getReceiverConnectionEvents(receiverUuid, token),
      getReceiverDailyBreakdown(receiverUuid, 'adsb', range.days, token),
      getReceiverHourlyBreakdown(receiverUuid, 'adsb', range.day, token),
      getReceiverHeatmap(receiverUuid, range.day, 'metric', token),
    ]);

    if (!fresh()) return;

    const ok = (i) => results[i].status === 'fulfilled' && isApiSuccess(results[i].value)
      ? results[i].value : null;

    const capsRes = ok(0);
    const statsRes = ok(2);

    setData(prev => ({
      ...prev,
      caps: capsRes,
      overview: ok(1),
      series: statsRes?.series || {},
      latest: ok(3),
      events: ok(4)?.events || [],
      daily: ok(5)?.buckets || [],
      hourly: ok(6)?.buckets || [],
      coverage: ok(7),
    }));

    const failed = results.filter(r => r.status === 'rejected');
    if (failed.length > 0) {
      setError(failed[0].reason?.message || 'Some statistics failed to load');
    }
    setUpdatedAt(new Date());
    setLoading(false);

    // ---- heavy queries, off the critical path ----

    const wantsAdsb = capsRes ? capsRes.adsbData !== false : true;
    if (wantsAdsb) {
      setReportLoading(true);
      getReceiverReportHeatmap(receiverUuid, range.heatmapDays, token)
        .then(res => { if (fresh()) setReportHeatmap(isApiSuccess(res) ? res : null); })
        .catch(() => { if (fresh()) setReportHeatmap(null); })
        .finally(() => { if (fresh()) setReportLoading(false); });

      // The day's tracked aircraft drives the 10-minute activity panel, and a
      // 10-minute-step range query drives the aligned furthest-contact panel.
      getTrackedAircraftList(receiverUuid, range.day, token)
        .then(res => {
          if (fresh() && isApiSuccess(res)) {
            setData(p => ({ ...p, dayAircraft: res.aircraft || [] }));
          }
        })
        .catch(() => { /* panel shows its own empty state */ });

      const dayFrom = `${range.day}T00:00:00.000Z`;
      const dayTo = `${range.day}T23:59:59.999Z`;
      getReceiverStats(receiverUuid, dayFrom, dayTo, 600, ['max_range_nm'], token)
        .then(res => {
          if (fresh() && isApiSuccess(res)) {
            setData(p => ({ ...p, dayRangeSamples: res.series?.max_range_nm || [] }));
          }
        })
        .catch(() => { /* panel shows its own empty state */ });
    } else {
      setReportHeatmap(null);
    }
  }, [receiverUuid, range]);

  useEffect(() => { load(); }, [load]);

  // Auto-refresh drives live health only; the caller decides the cadence.
  useEffect(() => {
    if (!refreshSeconds) return undefined;
    const id = setInterval(() => { load(); }, refreshSeconds * 1000);
    return () => clearInterval(id);
  }, [load, refreshSeconds]);

  return { ...data, reportHeatmap, reportLoading, loading, error, updatedAt, reload: load };
}

export default useReceiverStats;
